// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { SignalRProvider } from './contexts/SocketContext';
import GuestLoginPage from './pages/GuestLoginPage';
import MagicLoginPage from './pages/MagicLoginPage';
import HeyHotelLoginPage from './pages/HeyHotelLoginPage';
import AddGuestPage from './pages/AddGuestPage';
import GuestOnboardingPage from './pages/GuestOnboardingPage';
import CompleteGuestInfoPage from './pages/CompleteGuestInfoPage';
import AddReservationNotePage from './pages/AddReservationNotePage';
import AddCheckInTimePage from './pages/AddCheckInTimePage';
import DashboardPage from './pages/DashboardPage';
import ChatPage from './pages/ChatPage';
import CreateUserPage from './pages/CreateUserPage';
import UsersPage from './pages/UsersPage';
import RoomsPage from './pages/RoomsPage';
import SuperAdminPage from './pages/SuperAdminPage';
import HotelSettingsPage from './pages/HotelSettingsPage';
import PMSConfigPage from './pages/PMSConfigPage';
import PersonalSettingsPage from './pages/PersonalSettingsPage';
import ActivityReservationPage from './pages/ActivityReservationPage';
import ServiceTasksPage from './pages/ServiceTasksPage';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { Box, Typography, Avatar, Divider, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Button, Tooltip, Paper, FormControl, Select, MenuItem, SelectChangeEvent, Switch, useTheme, IconButton, Drawer, useMediaQuery } from '@mui/material';
import {
  Chat as ChatIcon, Archive as ArchiveIcon, People as PeopleIcon,
  Person as PersonIcon, Logout as LogoutIcon,
  Hotel as HotelIcon, Star as StarIcon, PersonAdd as PersonAddIcon,
  Dashboard as DashboardIcon, IntegrationInstructions as IntegrationIcon,
  MeetingRoom as RoomIcon, Bed as BedIcon, ConfirmationNumber as ConfirmationNumberIcon,
  CalendarToday as CalendarTodayIcon, Menu as MenuIcon, ChevronLeft as ChevronLeftIcon,
  Assignment as AssignmentIcon, Settings as SettingsIcon, Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon, LightMode as LightModeIcon, Language as LanguageIcon,
} from '@mui/icons-material';
import { useAuth } from './contexts/AuthContext';
import { UserRole, Reservation, Room } from './types/chat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { api } from './services/api';
import PMSLoginPage from './pages/PMSLoginPage';
import StaffLoginPage from './pages/StaffLoginPage';

// Flag country codes for languages
const languageFlags: Record<string, string> = {
  en: 'gb', // Great Britain
  tr: 'tr', // Turkey
  de: 'de', // Germany
  fr: 'fr', // France
  ru: 'ru', // Russia
  ar: 'ae', // UAE
};

// Flag icon component
const FlagIcon: React.FC<{ countryCode: string; size?: number }> = ({ countryCode, size = 24 }) => {
  return (
    <Box
      component="img"
      src={`https://flagcdn.com/w${size}/${countryCode}.png`}
      alt=""
      sx={{
        width: size,
        height: size * 0.75, // Maintain flag aspect ratio
        objectFit: 'cover',
        borderRadius: '2px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        border: '0.5px solid rgba(0, 0, 0, 0.1)',
      }}
      onError={(e) => {
        // Fallback to SVG if PNG fails
        const target = e.target as HTMLImageElement;
        target.src = `https://flagcdn.com/${countryCode}.svg`;
      }}
    />
  );
};

// --- THEME: HeyHotel mobile chat design tokens ---
const getTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: '#189BF0',
    },
    secondary: {
      main: '#9B5CE0',
    },
    background: {
      default: mode === 'light' ? '#EEF2F7' : '#121212',
      paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
    },
    text: {
      primary: mode === 'light' ? '#28324A' : '#ffffff',
      secondary: mode === 'light' ? '#8A94A8' : '#b0b0b0',
    },
  },
  typography: {
    fontFamily: ['"Plus Jakarta Sans"', 'system-ui', 'sans-serif'].join(','),
    h6: {
      fontWeight: 800,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { height: '100%' },
        body: {
          height: '100%',
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          WebkitTapHighlightColor: 'transparent',
        },
        '#root': { height: '100%' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(21, 41, 77, 0.05)'
            : '0 4px 12px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 700,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: mode === 'light'
            ? '1px solid #E4E9F1'
            : '1px solid rgba(255, 255, 255, 0.08)',
        }
      }
    }
  }
});

// --- PERSISTENT LEFT SIDEBAR COMPONENT (Mockup's far-left column) ---

// Reservation Summary Card Component
const ReservationSummaryCard: React.FC<{
  reservation: Reservation & { room?: Room },
  t: (key: string) => string
}> = ({ reservation, t }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    if (checkDate.getTime() === today.getTime()) {
      const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      return `${t('sidebar.today')}, ${time}`;
    }

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) + ', ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: '15px 15px 13px',
        borderRadius: '16px',
        bgcolor: theme.palette.background.paper,
        border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid #E4E9F1',
        mb: 2,
        boxShadow: isDarkMode ? undefined : '0 2px 8px rgba(21,41,77,.05)',
      }}
    >
      {/* Room and Reservation Numbers - hide room number when guest is in virtual room 000 (no physical room) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, justifyContent: 'space-between' }}>
        {/* Room Number - only show when guest has a real room (not 000) */}
        {reservation.room?.roomNumber && reservation.room.roomNumber !== '000' && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11.5px', fontWeight: 600, display: 'block' }}>
              {t('sidebar.yourRoomNumber')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: '3px' }}>
              <BedIcon sx={{ fontSize: 17, color: '#E0447C' }} />
              <Typography sx={{ fontWeight: 800, color: '#E0447C', fontSize: '19px', lineHeight: 1.2 }}>
                {reservation.room.roomNumber}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Reservation Number — confirm no from PMS when available, else GSC reservation id */}
        {(reservation.confirmationNumber || reservation.reservationId) && (
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11.5px', fontWeight: 600, display: 'block' }}>
            {t('sidebar.yourReservationNumber')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: '3px' }}>
            <ConfirmationNumberIcon sx={{ fontSize: 16, color: '#E0447C' }} />
            <Typography sx={{ fontWeight: 800, color: '#E0447C', fontSize: '19px', lineHeight: 1.2 }}>
              {reservation.confirmationNumber || reservation.reservationId}
            </Typography>
          </Box>
        </Box>
        )}
      </Box>

      {/* Check-in Date */}
      {reservation.checkInDate && (
      <Box
        sx={{
          bgcolor: isDarkMode ? 'rgba(30,158,86,0.15)' : '#E6F6EC',
          borderRadius: '11px',
          p: '10px 13px',
          mb: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: '#1E9E56', fontSize: '12.5px' }}>
          {t('sidebar.checkInDate')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CalendarTodayIcon sx={{ fontSize: 14, color: '#1E9E56' }} />
          <Typography sx={{ fontWeight: 700, color: '#1E9E56', fontSize: '12.5px' }}>
            {formatDate(reservation.checkInDate)}
          </Typography>
        </Box>
      </Box>
      )}

      {/* Check-out Date */}
      {reservation.checkOutDate && (
      <Box
        sx={{
          bgcolor: isDarkMode ? 'rgba(217,83,79,0.15)' : '#FCE9E7',
          borderRadius: '11px',
          p: '10px 13px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: '#D9534F', fontSize: '12.5px' }}>
          {t('sidebar.checkOutDate')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CalendarTodayIcon sx={{ fontSize: 14, color: '#D9534F' }} />
          <Typography sx={{ fontWeight: 700, color: '#D9534F', fontSize: '12.5px' }}>
            {formatDate(reservation.checkOutDate)}
          </Typography>
        </Box>
      </Box>
      )}
    </Paper>
  );
};

// Sidebar items - will be translated in component
const getSidebarItems = (t: (key: string) => string) => [
  { name: t('sidebar.superAdminDashboard'), icon: <DashboardIcon />, link: '/super-admin', superAdminOnly: true },
  { name: t('sidebar.guestChats'), icon: <ChatIcon />, link: '/dashboard', hideForSuperAdmin: true },
  { name: t('sidebar.holidayAssistant'), icon: <StarIcon />, link: '/dashboard/assistant', hideForSuperAdmin: true },
  { name: t('sidebar.archivedChats'), icon: <ArchiveIcon />, link: '/dashboard/archived', hideForSuperAdmin: true },
  { name: t('sidebar.staffChat'), icon: <ChatIcon />, link: '/dashboard/staff', staffOnly: true },
  { name: t('sidebar.serviceTasks'), icon: <AssignmentIcon />, link: '/service-tasks', staffOnly: true, hideForSuperAdmin: true },
];

const getSettingItems = (t: (key: string) => string) => [
  { name: t('sidebar.rooms'), icon: <RoomIcon />, link: '/rooms', adminOnly: true },
  { name: t('sidebar.createUser'), icon: <PersonAddIcon />, link: '/create-user', adminOnly: true },
  { name: t('sidebar.users'), icon: <PeopleIcon />, link: '/users', adminOnly: true },
  { name: t('sidebar.hotelSettings'), icon: <HotelIcon />, link: '/hotel-settings', adminOnly: true },
  { name: t('sidebar.pmsIntegration'), icon: <IntegrationIcon />, link: '/pms-config', adminOnly: true },
  { name: t('sidebar.personalSettings'), icon: <PersonIcon />, link: '/personal-settings' },
  { name: t('sidebar.activityReservation'), icon: <StarIcon />, link: '/activity-reservation' },
];

const Sidebar: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accentBlue = '#189BF0';
  const accentBlack = '#15294D';
  const accentIce = '#EAF6FE';

  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [reservationData, setReservationData] = useState<(Reservation & { room?: Room }) | null>(null);
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const settingsButtonRef = useRef<HTMLButtonElement | null>(null);
  const settingsBubbleRef = useRef<HTMLDivElement | null>(null);
  const [soundNotifications, setSoundNotifications] = useState(() => {
    const saved = localStorage.getItem('soundNotifications');
    return saved !== null ? saved === 'true' : true;
  });
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved === 'dark' || saved === 'light') ? saved : 'light';
  });

  // Sync with i18n language changes
  useEffect(() => {
    const updateLanguage = () => {
      setCurrentLang(i18n.language);
    };

    i18n.on('languageChanged', updateLanguage);
    updateLanguage(); // Initial sync

    return () => {
      i18n.off('languageChanged', updateLanguage);
    };
  }, [i18n]);

  // Get the base language code (e.g., 'en' from 'en-US' or 'en-GB')
  const getLanguageCode = (lang: string) => {
    return lang.split('-')[0].toLowerCase();
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLang = event.target.value;
    i18n.changeLanguage(newLang).catch((err: unknown) => {
      console.error('Error changing language:', err);
    });
  };

  const handleSoundNotificationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setSoundNotifications(enabled);
    localStorage.setItem('soundNotifications', enabled.toString());
  };

  const handleThemeChange = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    localStorage.setItem('themeMode', newMode);
    // Update theme in parent component
    window.dispatchEvent(new CustomEvent('themeChange', { detail: newMode }));
  };

  // Close settings bubble when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsOpen && !languageMenuOpen) {
        const target = event.target as HTMLElement;
        const isClickInsideButton = settingsButtonRef.current?.contains(target);
        const isClickInsideBubble = settingsBubbleRef.current?.contains(target);

        if (!isClickInsideButton && !isClickInsideBubble) {
          setSettingsOpen(false);
        }
      }
    };

    if (settingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [settingsOpen, languageMenuOpen]);
  const isSelected = (link: string) => {
    if (link === '/dashboard') {
      return location.pathname === '/dashboard' || (location.pathname.startsWith('/dashboard/chat/'));
    }
    return location.pathname === link || location.pathname.startsWith(link + '/');
  };

  // Fetch reservation data for guests — prefer live PMS confirmation number over local/stale IDs
  useEffect(() => {
    const fetchReservationData = async () => {
      if (user?.role === UserRole.GUEST) {
        const mapGscReservation = async (
          gscData: any,
          gscReservation: any,
          roomNumberFromProfile?: string,
        ): Promise<(Reservation & { room?: Room }) | null> => {
          if (!gscReservation) return null;

          const checkInDate =
            gscReservation.arrivalDate ||
            gscReservation.checkInDate ||
            gscReservation.CheckInDate ||
            '';
          const checkOutDate =
            gscReservation.departureDate ||
            gscReservation.checkOutDate ||
            gscReservation.CheckOutDate ||
            '';

          const roomNumber: string | undefined =
            roomNumberFromProfile ||
            gscReservation.roomNumber ||
            gscReservation.RoomNumber ||
            gscReservation.roomNo ||
            gscReservation.RoomNo;

          let roomData: Room | undefined = undefined;
          if (user.roomId) {
            try {
              const roomResponse = await api.get(`/rooms/${user.roomId}`);
              roomData = roomResponse.data;
              if (roomNumber && roomData && roomData.roomNumber !== roomNumber) {
                roomData = { ...roomData, roomNumber } as Room;
              }
            } catch (roomError) {
              console.error('Error fetching room data by roomId:', roomError);
              if (roomNumber) {
                roomData = {
                  id: user.roomId,
                  roomNumber,
                  status: gscReservation.computedStatus || gscReservation.status || 'active',
                } as Room;
              }
            }
          } else if (roomNumber) {
            roomData = {
              id: '',
              roomNumber,
              status: gscReservation.computedStatus || gscReservation.status || 'active',
            } as Room;
          }

          // Prefer PMS confirmation number; never label raw reservationId as confirmation.
          const confirmationNumber =
            gscData.confirmationNumber ||
            gscReservation.confirmationNumber ||
            gscReservation.ConfirmationNumber ||
            '';

          return {
            id: gscData.reservationID || user.reservationId || '',
            confirmationNumber,
            reservationId: gscData.reservationID || user.reservationId,
            profileId: gscData.profileID,
            checkInDate,
            checkOutDate,
            status: gscReservation.computedStatus || gscReservation.status || 'active',
            guestId: user.id,
            roomId: user.roomId || '',
            room: roomData,
          } as Reservation & { room?: Room };
        };

        const fetchFromPms = async (): Promise<(Reservation & { room?: Room }) | null> => {
          if (!user.reservationId) return null;
          try {
            let roomNumberFromProfile: string | undefined;
            if (user.profileId) {
              try {
                const profileResponse = await api.get(`/users/guest-profile-from-gsc/${user.profileId}`, {
                  params: { reservationId: user.reservationId },
                });
                if (profileResponse.data?.roomNumber) {
                  roomNumberFromProfile = profileResponse.data.roomNumber;
                }
              } catch (profileError) {
                console.error('Error fetching room number from GuestProfile:', profileError);
              }
            }

            const gscResponse = await api.get('/gsc-api/guest-reservation-info', {
              params: { confirmationNumber: user.reservationId },
            });
            const payload = gscResponse.data;
            if (payload?.success && payload?.data?.reservation) {
              return mapGscReservation(payload.data, payload.data.reservation, roomNumberFromProfile);
            }
          } catch (gscError) {
            console.error('Error fetching reservation data from PMS (GSC API):', gscError);
          }
          return null;
        };

        // 1) Live PMS first (canonical confirmation + dates)
        const fromPms = await fetchFromPms();
        if (fromPms) {
          setReservationData(fromPms);
          return;
        }

        // 2) Auth user.reservation if already attached
        if (user.reservation) {
          const reservation = user.reservation as Reservation;
          if (reservation.roomId) {
            try {
              const roomResponse = await api.get(`/rooms/${reservation.roomId}`);
              setReservationData({
                ...reservation,
                room: roomResponse.data,
              } as Reservation & { room?: Room });
            } catch (roomError) {
              console.error('Error fetching room data:', roomError);
              setReservationData(reservation as Reservation & { room?: Room });
            }
          } else {
            setReservationData(reservation as Reservation & { room?: Room });
          }
          return;
        }

        // 3) Local DB by UUID (rarely matches — user.reservationId is usually GSC id)
        if (user.reservationId) {
          try {
            const response = await api.get(`/reservations/${user.reservationId}`);
            if (response.data) {
              if (response.data.roomId) {
                try {
                  const roomResponse = await api.get(`/rooms/${response.data.roomId}`);
                  setReservationData({
                    ...response.data,
                    room: roomResponse.data,
                  } as Reservation & { room?: Room });
                } catch (roomError) {
                  console.error('Error fetching room data:', roomError);
                  setReservationData(response.data);
                }
              } else {
                setReservationData(response.data);
              }
              return;
            }
          } catch (error) {
            console.error('Error fetching reservation data:', error);
          }

          // 4) Minimal fallback — show reservationId only if we have nothing else
          if (user.roomId) {
            try {
              const roomResponse = await api.get(`/rooms/${user.roomId}`);
              setReservationData({
                id: user.reservationId || '',
                confirmationNumber: user.reservationId || '',
                checkInDate: '',
                checkOutDate: '',
                status: 'active',
                guestId: user.id,
                roomId: user.roomId,
                room: roomResponse.data,
              } as Reservation & { room?: Room });
            } catch (roomError) {
              console.error('Error fetching room data as last fallback:', roomError);
            }
          } else if (user.reservationId) {
            setReservationData({
              id: user.reservationId,
              confirmationNumber: user.reservationId,
              status: 'active',
              guestId: user.id,
              roomId: '',
            } as Reservation & { room?: Room });
          }
        }
      }
    };

    fetchReservationData();
    // reservationData read only to avoid overwriting existing state on re-fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const open = () => setMobileOpen(true);
    const close = () => setMobileOpen(false);
    const toggle = () => setMobileOpen((v) => !v);
    window.addEventListener('pms-mobile-drawer-open', open);
    window.addEventListener('pms-mobile-drawer-close', close);
    window.addEventListener('pms-mobile-drawer-toggle', toggle);
    return () => {
      window.removeEventListener('pms-mobile-drawer-open', open);
      window.removeEventListener('pms-mobile-drawer-close', close);
      window.removeEventListener('pms-mobile-drawer-toggle', toggle);
    };
  }, []);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const sidebarItems = getSidebarItems(t);
  const settingItems = getSettingItems(t);

  // Filter sidebar items based on user role
  const visibleSidebarItems = sidebarItems.filter(item => {
    // Super Admin only items
    if ((item as any).superAdminOnly && user?.role !== UserRole.SUPER_ADMIN) {
      return false;
    }
    // Hide chat items from Super Admins
    if (user?.role === UserRole.SUPER_ADMIN && (item as any).hideForSuperAdmin) {
      return false;
    }
    // Staff only items (operators and admins only)
    if ((item as any).staffOnly && user?.role === UserRole.GUEST) {
      return false;
    }
    // Super Admins see their own items
    if (user?.role === UserRole.SUPER_ADMIN) {
      return (item as any).superAdminOnly;
    }
    // Guests only see their own chats
    if (user?.role === UserRole.GUEST) {
      return item.name === t('sidebar.guestChats');
    }
    // Operators and Admins see all items (except super admin only)
    return !(item as any).superAdminOnly;
  });

  const visibleSettingItems = settingItems.filter(item => {
    // Super Admins see minimal settings (they have the super admin page)
    if (user?.role === UserRole.SUPER_ADMIN) {
      return item.name === t('sidebar.personalSettings');
    }

    // Check if item is admin only
    if ((item as any).adminOnly && user?.role !== UserRole.ADMIN) {
      return false;
    }

    // Only admins see user management and hotel settings
    if (user?.role === UserRole.ADMIN) {
      return true;
    }
    // Operators see personal settings and activity reservation
    if (user?.role === UserRole.OPERATOR) {
      return item.name === t('sidebar.personalSettings') || item.name === t('sidebar.activityReservation');
    }
    // Guests only see personal settings
    return item.name === t('sidebar.personalSettings');
  });

  const sidebarPanel = (
    <Box
      sx={{
        width: isMobile ? '100%' : (isCollapsed ? 80 : 280),
        maxWidth: isMobile ? 310 : undefined,
        flexShrink: 0,
        background: theme.palette.background.paper,
        height: '100%',
        borderRight: isMobile ? 'none' : (isDarkMode
          ? `1px solid ${theme.palette.divider}`
          : '1px solid #E4E9F1'),
        p: isMobile ? '18px 16px 14px' : (isCollapsed ? 2 : 3),
        pt: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 18px)' : undefined,
        pb: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 14px)' : undefined,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isMobile
          ? '8px 0 32px rgba(21,41,77,.14)'
          : (isDarkMode ? '4px 0 24px rgba(0, 0, 0, 0.5)' : '4px 0 24px rgba(21, 41, 77, 0.08)'),
        position: 'relative',
        overflow: 'hidden',
        transition: 'width 0.3s ease, padding 0.3s ease',
        bgcolor: theme.palette.background.paper,
      }}
    >
      {/* Top Section: Branding and Profile */}
      <Box sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed && !isMobile ? 'center' : 'space-between',
          mb: 3
        }}>
          {(!isCollapsed || isMobile) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/heyhotelai-logo.png"
                alt="HeyHotel.AI"
                sx={{ height: 28, width: 'auto', objectFit: 'contain' }}
              />
            </Box>
          )}
          {!isMobile && (
          <Tooltip title={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}>
            <Button
              onClick={() => {
                const newState = !isCollapsed;
                setIsCollapsed(newState);
                localStorage.setItem('sidebarCollapsed', newState.toString());
              }}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: isDarkMode ? theme.palette.action.hover : accentIce,
                color: accentBlue,
                '&:hover': {
                  bgcolor: isDarkMode ? theme.palette.action.selected : 'rgba(24, 155, 240, 0.18)',
                },
              }}
            >
              {isCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
            </Button>
          </Tooltip>
          )}
        </Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          p: isCollapsed && !isMobile ? 1 : '13px 14px',
          borderRadius: '14px',
          background: isDarkMode ? theme.palette.action.hover : accentIce,
          border: isDarkMode
            ? `1px solid ${theme.palette.divider}`
            : '1px solid #D3ECFD',
          transition: 'all 0.3s ease',
          justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
        }}>
          <Tooltip title={`${user?.firstName} ${user?.lastName} - ${user?.role || 'Unknown'}`}>
            <Avatar
              src={user?.profilePicture}
              sx={{
                background: 'linear-gradient(135deg, #1D7FA8 0%, #189BF0 100%)',
                bgcolor: '#DDF0F6',
                color: '#1D7FA8',
                mr: isCollapsed && !isMobile ? 0 : 1.5,
                width: 44,
                height: 44,
                fontWeight: 800,
              }}
            >
              {!user?.profilePicture && (
                <>{user?.firstName.charAt(0)}{user?.lastName.charAt(0)}</>
              )}
            </Avatar>
          </Tooltip>
          {(!isCollapsed || isMobile) && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '15px', color: accentBlack }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography sx={{
                color: theme.palette.text.secondary,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.25,
                fontSize: '12.5px',
              }}>
                <PersonIcon sx={{ fontSize: 13 }} />
                {user?.role === UserRole.SUPER_ADMIN && t('sidebar.superAdmin')}
                {user?.role === UserRole.ADMIN && t('sidebar.admin')}
                {user?.role === UserRole.OPERATOR && t('sidebar.operator')}
                {user?.role === UserRole.GUEST && t('sidebar.guest')}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider sx={{
          mt: 2,
          borderColor: isDarkMode ? theme.palette.divider : '#E4E9F1',
        }} />
      </Box>

      {/* Main Navigation Links */}
      <List dense sx={{
        flexGrow: 1,
        position: 'relative',
        zIndex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        pr: 1,
        '&::-webkit-scrollbar': {
          display: 'none', // Hide scrollbar for Chrome, Safari and Opera
        },
        scrollbarWidth: 'none', // Hide scrollbar for Firefox
        msOverflowStyle: 'none', // Hide scrollbar for IE and Edge
      }}>
        {visibleSidebarItems.map((item, index) => {
          const isStaffChat = item.link === '/dashboard/staff';
          const isServiceTasks = item.link === '/service-tasks';

          return (
            <React.Fragment key={item.name}>
              {/* Show STAFF CHAT heading only before staff chat item */}
              {isStaffChat && (!isCollapsed || isMobile) && (
                <>
                  <Divider sx={{
                    my: 2,
                    borderColor: isDarkMode ? theme.palette.divider : 'rgba(0, 0, 0, 0.08)',
                  }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1.5,
                      ml: 1.5,
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      color: theme.palette.text.secondary,
                      letterSpacing: '0.05em',
                      fontSize: '0.7rem',
                    }}
                  >
                    {t('sidebar.staffChatSection', 'STAFF CHAT')}
                  </Typography>
                </>
              )}
              {/* Show SERVICE TASKS heading before service tasks item */}
              {isServiceTasks && (!isCollapsed || isMobile) && (
                <>
                  <Divider sx={{
                    my: 2,
                    borderColor: isDarkMode ? theme.palette.divider : 'rgba(0, 0, 0, 0.08)',
                  }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1.5,
                      ml: 1.5,
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      color: theme.palette.text.secondary,
                      letterSpacing: '0.05em',
                      fontSize: '0.7rem',
                    }}
                  >
                    {t('sidebar.serviceTasksSection')}
                  </Typography>
                </>
              )}
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <Tooltip title={isCollapsed && !isMobile ? item.name : ''} placement="right">
                  <ListItemButton
                    onClick={() => {
                      navigate(item.link);
                      if (isMobile) setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: '12px',
                      mb: 0.5,
                      justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
                      backgroundColor: isSelected(item.link)
                        ? (isDarkMode ? 'rgba(24, 155, 240, 0.25)' : '#DDEFFC')
                        : 'transparent',
                      border: '1px solid transparent',
                      transition: 'all 0.2s ease',
                      py: 1.25,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(24, 155, 240, 0.2)' : '#DDEFFC',
                        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                          color: '#1877C2 !important',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: isCollapsed && !isMobile ? 'auto' : 40,
                      color: isSelected(item.link) ? '#1877C2' : theme.palette.text.secondary,
                      transition: 'color 0.2s ease',
                      justifyContent: 'center',
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {(!isCollapsed || isMobile) && (
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: 700,
                          fontSize: '14px',
                          color: isSelected(item.link) ? '#1877C2' : theme.palette.text.primary,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            </React.Fragment>
          );
        })}

        <Divider sx={{
          my: 2,
          borderColor: isDarkMode ? theme.palette.divider : '#E4E9F1',
        }} />

        {visibleSettingItems.length > 0 && (
          <>
            {(!isCollapsed || isMobile) && (
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  ml: 1.5,
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  color: '#98A2B6',
                  letterSpacing: '0.09em',
                  fontSize: '11.5px',
                }}
              >
                {t('sidebar.settings')}
              </Typography>
            )}
            {visibleSettingItems.map((item) => (
              <ListItem key={item.name} disablePadding sx={{ py: 0.5 }}>
                <Tooltip title={isCollapsed && !isMobile ? item.name : ''} placement="right">
                  <ListItemButton
                    onClick={() => {
                      navigate(item.link);
                      if (isMobile) setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: '12px',
                      mb: 0.5,
                      justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
                      transform: 'none',
                      backgroundColor: isSelected(item.link)
                        ? 'rgba(0, 174, 239, 0.25)'
                        : 'transparent',
                      border: isSelected(item.link)
                        ? '1px solid rgba(0, 174, 239, 0.6)'
                        : '1px solid transparent',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: accentBlue,
                        transform: isCollapsed ? 'scale(0.95)' : 'scale(0.9) translateX(4px)',
                        borderColor: isSelected(item.link)
                          ? 'rgba(0, 174, 239, 0.8)'
                          : 'rgba(0, 174, 239, 0.5)',
                        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                          color: '#fff !important',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: isCollapsed ? 'auto' : 40,
                      color: isSelected(item.link) ? accentBlue : theme.palette.text.secondary,
                      transition: 'color 0.3s ease',
                      justifyContent: 'center',
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {(!isCollapsed || isMobile) && (
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: isSelected(item.link) ? 700 : 500,
                          color: isSelected(item.link) ? accentBlue : theme.palette.text.primary,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}
      </List>

      {/* Bottom Section: Settings and Footer */}
      <Box sx={{
        pt: 2,
        borderTop: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid #E4E9F1',
        position: 'relative',
        zIndex: 1,
        mt: 'auto',
      }}>
        {/* Reservation Summary Card - Only for Guests, right above Settings */}
        {user?.role === UserRole.GUEST && reservationData && (!isCollapsed || isMobile) && (
          <Box sx={{ mb: 2 }}>
            <ReservationSummaryCard reservation={reservationData} t={t} />
          </Box>
        )}

        {/* Settings Section and Version - Side by Side */}
        {(!isCollapsed || isMobile) && (
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1.5 }}>
            <Box sx={{ position: 'relative' }}>
              <IconButton
                data-settings-button
                ref={settingsButtonRef}
                onClick={() => setSettingsOpen(!settingsOpen)}
                sx={{
                  borderRadius: 2,
                  color: settingsOpen ? accentBlue : theme.palette.text.secondary,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    bgcolor: settingsOpen
                      ? 'rgba(0, 174, 239, 0.2)'
                      : accentIce,
                    color: accentBlue,
                  },
                }}
              >
                <SettingsIcon sx={{ fontSize: 20 }} />
              </IconButton>

              {/* Settings Bubble - Absolute positioned above the button */}
              {settingsOpen && (
                <Paper
                  data-settings-bubble
                  ref={settingsBubbleRef}
                  elevation={8}
                  sx={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '150%',
                    transform: 'translateX(-40%)',
                    mb: 1,
                    width: 240,
                    bgcolor: theme.palette.background.paper,
                    backdropFilter: 'blur(20px)',
                    border: isDarkMode
                      ? `1px solid ${theme.palette.divider}`
                      : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: 3,
                    boxShadow: isDarkMode
                      ? '0 8px 24px rgba(0, 0, 0, 0.5)'
                      : '0 8px 24px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    overflow: 'hidden',
                    animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@keyframes slideUp': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(-40%) translateY(10px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(-40%) translateY(0)',
                      },
                    },
                  }}
                >
                  {/* Settings Content */}
                  <Box sx={{ p: 1.5 }}>

                    {/* Sound Notifications */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: isDarkMode
                        ? `1px solid ${theme.palette.divider}`
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: soundNotifications ? '#667eea' : theme.palette.text.secondary,
                        }}>
                          <NotificationsIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.25, fontSize: '0.875rem' }}>
                            {t('sidebar.soundNotifications')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.75rem' }}>
                            {soundNotifications ? t('sidebar.on') : t('sidebar.off')}
                          </Typography>
                        </Box>
                      </Box>
                      <Switch
                        checked={soundNotifications}
                        onChange={handleSoundNotificationsChange}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#667eea',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#667eea',
                          },
                        }}
                      />
                    </Box>

                    {/* Language Selector */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: isDarkMode
                        ? `1px solid ${theme.palette.divider}`
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.text.secondary,
                        }}>
                          <LanguageIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.25, fontSize: '0.875rem' }}>
                            {t('sidebar.language')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FlagIcon countryCode={languageFlags[getLanguageCode(currentLang)] || languageFlags[currentLang] || 'gb'} size={14} />
                            <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 700, fontSize: '0.75rem' }}>
                              {currentLang === 'tr' ? 'Türkçe' :
                                currentLang === 'en' ? 'English' :
                                  currentLang === 'de' ? 'Deutsch' :
                                    currentLang === 'fr' ? 'Français' :
                                      currentLang === 'ru' ? 'Русский' :
                                        currentLang === 'ar' ? 'العربية' : currentLang}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 40 }}>
                        <Select
                          value={currentLang}
                          onChange={handleLanguageChange}
                          onOpen={() => setLanguageMenuOpen(true)}
                          onClose={() => setLanguageMenuOpen(false)}
                          IconComponent={() => null}
                          renderValue={(value) => {
                            const langCode = getLanguageCode(value || currentLang);
                            const countryCode = languageFlags[langCode] || languageFlags[value] || 'gb';
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FlagIcon countryCode={countryCode} size={18} />
                              </Box>
                            );
                          }}
                          sx={{
                            '& .MuiSelect-select': {
                              padding: '2px 6px !important',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            },
                            bgcolor: 'transparent',
                            border: 'none',
                            '&:hover': {
                              bgcolor: 'rgba(102, 126, 234, 0.05)',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 2,
                                mt: 0.5,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              },
                            },
                          }}
                        >
                          <MenuItem value="en">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                              <FlagIcon countryCode={languageFlags.en} size={24} />
                            </Box>
                          </MenuItem>
                          <MenuItem value="tr">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                              <FlagIcon countryCode={languageFlags.tr} size={24} />
                            </Box>
                          </MenuItem>
                          <MenuItem value="de">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                              <FlagIcon countryCode={languageFlags.de} size={24} />
                            </Box>
                          </MenuItem>
                          <MenuItem value="fr">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                              <FlagIcon countryCode={languageFlags.fr} size={24} />
                            </Box>
                          </MenuItem>
                          <MenuItem value="ru">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                              <FlagIcon countryCode={languageFlags.ru} size={24} />
                            </Box>
                          </MenuItem>
                          <MenuItem value="ar">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                              <FlagIcon countryCode={languageFlags.ar} size={24} />
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Theme Toggle — hidden for guests (mobile UX) */}
                    {user?.role !== UserRole.GUEST && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: themeMode === 'light' ? '#fbbf24' : '#9ca3af',
                        }}>
                          {themeMode === 'light' ? <LightModeIcon sx={{ fontSize: 20 }} /> : <DarkModeIcon sx={{ fontSize: 20 }} />}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.25, fontSize: '0.875rem' }}>
                            {t('sidebar.theme')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 700, fontSize: '0.75rem' }}>
                            {themeMode === 'light' ? t('sidebar.light') : t('sidebar.dark')}
                          </Typography>
                        </Box>
                      </Box>
                      <Switch
                        checked={themeMode === 'dark'}
                        onChange={handleThemeChange}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#667eea',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#667eea',
                          },
                        }}
                      />
                    </Box>
                    )}

                    {/* Logout Button */}
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={logout}
                      startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        py: 1,
                        mt: 1,
                        fontSize: '0.875rem',
                        borderColor: '#ef4444',
                        color: '#ef4444',
                        background: 'rgba(239, 68, 68, 0.05)',
                        '&:hover': {
                          borderColor: '#ef4444',
                          background: 'rgba(239, 68, 68, 0.1)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {t('sidebar.logout')}
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: '#9ca3af',
                fontWeight: 500,
              }}
            >
              HEYHOTEL.AI v1.0.0
            </Typography>
          </Box>
        )}

        {/* Collapsed Settings Button */}
        {isCollapsed && (
          <Tooltip title={t('sidebar.settings')} placement="right">
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setSettingsOpen(!settingsOpen)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                py: 1.25,
                minWidth: 40,
                borderColor: `rgba(0, 174, 239, ${settingsOpen ? 0.6 : 0.4})`,
                color: accentBlue,
                background: settingsOpen ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                '&:hover': {
                  borderColor: accentBlue,
                  background: 'rgba(0, 174, 239, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 1,
              }}
            >
              <SettingsIcon sx={{ transform: 'scale(0.9)' }} />
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 'min(310px, 84%)',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            boxShadow: '8px 0 32px rgba(21,41,77,.14)',
          },
        }}
        slotProps={{
          backdrop: {
            sx: { bgcolor: 'rgba(21,41,77,.42)' },
          },
        }}
      >
        {sidebarPanel}
      </Drawer>
    );
  }

  return sidebarPanel;
};


// --- MAIN LAYOUT COMPONENT ---
interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated || location.pathname === '/login' || location.pathname === '/login-staff') {
    return <>{children}</>;
  }

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100dvh',
      height: '100dvh',
      bgcolor: theme.palette.background.default,
      overflow: 'hidden',
    }}>
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          display: 'flex',
          maxHeight: '100dvh',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
          width: isMobile ? '100%' : undefined,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};


function App() {
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved === 'dark' || saved === 'light') ? saved : 'light';
  });

  useEffect(() => {
    const handleThemeChange = (event: CustomEvent) => {
      setThemeMode(event.detail);
    };

    window.addEventListener('themeChange', handleThemeChange as EventListener);
    return () => {
      window.removeEventListener('themeChange', handleThemeChange as EventListener);
    };
  }, []);

  const theme = getTheme(themeMode);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SignalRProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<GuestLoginPage />} />
                <Route path="/HeyHotelLoginURL/:hotelCode" element={<HeyHotelLoginPage />} />
                <Route path="/HeyHotelLoginURL/:hotelCode/:reservationId/:profileId" element={<HeyHotelLoginPage />} />
                <Route path="/login-staff" element={<StaffLoginPage />} />
                <Route path="/pms-login" element={<PMSLoginPage />} />
                <Route path="/magic-login" element={<MagicLoginPage />} />
                <Route path="/add-guest" element={<AddGuestPage />} />
                <Route path="/guest-onboarding" element={<GuestOnboardingPage />} />
                <Route path="/complete-guest-info" element={<CompleteGuestInfoPage />} />
                <Route path="/add-reservation-note" element={<AddReservationNotePage />} />
                <Route path="/add-check-in-time" element={<AddCheckInTimePage />} />
                <Route path="*" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Routes>
                        {/* DashboardPage with nested chat route and filtered views */}
                        <Route path="/dashboard" element={<DashboardPage />}>
                          <Route path="chat/:id" element={<ChatPage />} />
                        </Route>
                        <Route path="/dashboard/assistant" element={<DashboardPage />}>
                          <Route path="chat/:id" element={<ChatPage />} />
                        </Route>
                        <Route path="/dashboard/staff" element={<DashboardPage />}>
                          <Route path="chat/:id" element={<ChatPage />} />
                        </Route>
                        <Route path="/dashboard/archived" element={<DashboardPage />}>
                          <Route path="chat/:id" element={<ChatPage />} />
                        </Route>
                        {/* Super Admin Page */}
                        <Route
                          path="/super-admin"
                          element={
                            <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                              <SuperAdminPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* Rooms Management Page - Admin Only */}
                        <Route
                          path="/rooms"
                          element={
                            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                              <RoomsPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* Create User Page - Admin Only */}
                        <Route
                          path="/create-user"
                          element={
                            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                              <CreateUserPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* Users Management Page - Admin Only */}
                        <Route
                          path="/users"
                          element={
                            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                              <UsersPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* Hotel Settings Page - Admin Only */}
                        <Route
                          path="/hotel-settings"
                          element={
                            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                              <HotelSettingsPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* PMS Configuration Page - Admin Only */}
                        <Route
                          path="/pms-config"
                          element={
                            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                              <PMSConfigPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* Personal Settings Page - All authenticated users */}
                        <Route
                          path="/personal-settings"
                          element={<PersonalSettingsPage />}
                        />
                        {/* Activity Reservation Page - All authenticated users */}
                        <Route
                          path="/activity-reservation"
                          element={<ActivityReservationPage />}
                        />
                        {/* Service Tasks Page - Admin and Operator */}
                        <Route
                          path="/service-tasks"
                          element={
                            <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.OPERATOR]}>
                              <ServiceTasksPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/" element={<Navigate to="/dashboard" />} />
                      </Routes>
                    </MainLayout>
                  </ProtectedRoute>
                } />
              </Routes>
            </Router>
          </SignalRProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;